import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Checkbox } from "@solomon-apps/shared/src/components/ui/checkbox";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger
} from "@solomon-apps/shared/src/components/ui/select";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    ToggleGroup,
    ToggleGroupItem
} from "@solomon-apps/shared/src/components/ui/toggle-group";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";

import {
    PROGRAM_POLICIES_QUERY_KEY,
    useCreateProgramPolicy,
    useProgramPolicies,
    useUpdateProgramPolicyValue
} from "@/hooks/useProgramPolicies";
import { clientEffectivePolicyToPolicyDefinition } from "@/services/clientProgramPresentation";
import type { ProgramPoliciesResponse } from "@/types/programPolicies";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import {
    PERIOD_OPTIONS,
    type Program,
    type ProgramStatus
} from "../shared/programMocks";

import { ConstraintSummary } from "./ConstraintSummary";
import {
    buildPolicyParameters,
    CATEGORY_LABELS,
    CATEGORY_ORDER,
    PERMISSION_LABELS,
    POLICY_LIBRARY,
    POLICY_TYPE_LIST,
    type PolicyCategory,
    type PolicyDefinition,
    type PolicyTypeOption
} from "./policiesMocks";
import { PolicyConfigEditor } from "./PolicyConfigEditor";
import { type PolicyEditorSummary, resolvePolicyEditor } from "./policyEditors";

type CategoryFilter = PolicyCategory | "all";
type ActivityFilter = "all" | "active" | "inactive";

const CATEGORY_OPTIONS: { value: CategoryFilter; label: string }[] = [
    { value: "all", label: "All Categories" },
    ...CATEGORY_ORDER.map((cat) => ({
        value: cat,
        label: CATEGORY_LABELS[cat]
    })).sort((a, b) => a.label.localeCompare(b.label))
];

const ACTIVITY_OPTIONS: { value: ActivityFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" }
];

const EMPTY_CREATE_SUMMARY: PolicyEditorSummary = {
    serialized: "",
    dirty: false,
    valid: false,
    minViolated: false,
    maxViolated: false,
    changeLines: null,
    errorLine: null,
    reset: () => undefined
};

/** Program status → indication state, for the "Programs attached to" rows. */
const PROGRAM_STATE: Record<ProgramStatus, "success" | "pending" | "disabled"> =
    {
        active: "success",
        in_progress: "pending",
        ended: "disabled"
    };

/** Days remaining for a fixed-length program (null if continuous/over). */
function programDaysLeft(program: Program): number | null {
    if ((program.programType ?? "fixed") !== "fixed") return null;
    const period = PERIOD_OPTIONS.find((o) => o.value === program.period);
    if (!period) return null;
    const left = period.days - (program.daysElapsed ?? 0);
    return left > 0 ? left : null;
}

function programsForPolicy(
    policy: PolicyDefinition,
    programs: Program[]
): Program[] {
    return programs.filter((program) => policy.programIds.includes(program.id));
}

function policyIsActive(
    policy: PolicyDefinition,
    programs: Program[]
): boolean {
    return programsForPolicy(policy, programs).some(
        (program) => program.status === "active"
    );
}

function initialCreateValueForType(type: PolicyTypeOption): string {
    if (type.constraint.kind === "options") {
        return type.constraint.options[0] ?? "";
    }
    if (type.category === "tranche") return "3 tranches";
    return String(type.constraint.min);
}

function createDraftPolicyDefinition(type: PolicyTypeOption): PolicyDefinition {
    return {
        id: `new-policy-${type.typeId}`,
        typeId: type.typeId,
        type: type.label,
        category: type.category,
        value: initialCreateValueForType(type),
        detail: type.detail,
        programIds: [],
        constraint: type.constraint,
        // Client-created wallet lists should be editable in the draft UI even
        // when the seeded standing issuer list is locked.
        permission:
            type.typeId === "wallet-list" ? "delegated" : type.permission,
        wallets: type.constraint.kind === "options" ? [] : undefined
    };
}

/**
 * Policies — a master-detail split screen. Left rail: a Category dropdown,
 * an Active/Inactive status toggle, and the policy library list (the
 * "sub-categories" — the actual policies, filtered by the chosen
 * category). Active = used by an active Program; Inactive = not.
 * Selecting a policy opens its detail: rule + the YaaS surfaces it
 * applies to.
 */
export function Policies(): React.ReactElement {
    // Real data layer — query hook (falls back to mock until the endpoint
    // is built) + a scope-targeted create mutation.
    const queryClient = useQueryClient();
    const { data } = useProgramPolicies();
    const instances = data.policies;
    const availablePrograms = data.programs;
    const createPolicy = useCreateProgramPolicy();
    const updatePolicy = useUpdateProgramPolicyValue();

    const [creating, setCreating] = useState(false);

    const [category, setCategory] = useState<CategoryFilter>("all");
    const [policyFilter, setPolicyFilter] = useState<string>("all");
    const [activity, setActivity] = useState<ActivityFilter>("all");
    const [selectedId, setSelectedId] = useState<string>(POLICY_LIBRARY[0].id);

    const handleCreate = async (
        typeId: string,
        raw: string,
        programIds: string[]
    ): Promise<void> => {
        const type = POLICY_TYPE_LIST.find((t) => t.typeId === typeId);
        if (!type || programIds.length === 0) return;
        const result = await createPolicy
            .mutateAsync({
                typeId,
                parameters: buildPolicyParameters(typeId, type.constraint, raw),
                scopeType: "Program",
                scopeTargetUuids: programIds
            })
            .catch((error) => {
                throw new Error(
                    resolveClientFormError(
                        error,
                        "policy",
                        "Policy was not created. Check the value and distributor constraints, then try again."
                    )
                );
            });
        const created = clientEffectivePolicyToPolicyDefinition(
            result.policy,
            programIds[0]
        );
        created.programIds = Array.from(
            new Set([...created.programIds, ...programIds])
        );
        queryClient.setQueryData<ProgramPoliciesResponse>(
            PROGRAM_POLICIES_QUERY_KEY,
            (prev) => {
                const base = prev ?? data;
                return {
                    ...base,
                    policies: [
                        ...base.policies.filter((p) => p.id !== created.id),
                        created
                    ],
                    snapshotAt: result.snapshotAt
                };
            }
        );
        setSelectedId(created.id);
        setCreating(false);
    };

    // Edit-in-place: change an instance's value, validated against the
    // issuer constraint, then scope-targeted save.
    const handleUpdate = async (
        policy: PolicyDefinition,
        raw: string
    ): Promise<void> => {
        const result = await updatePolicy
            .mutateAsync({
                policyUuid: policy.id,
                parameters: buildPolicyParameters(
                    policy.typeId,
                    policy.constraint,
                    raw
                ),
                scopeType: "Program",
                scopeTargetUuids: policy.programIds
            })
            .catch((error) => {
                throw new Error(
                    resolveClientFormError(
                        error,
                        "policy",
                        "Policy was not saved. Check the value and distributor constraints, then try again."
                    )
                );
            });
        const updated = clientEffectivePolicyToPolicyDefinition(
            result.policy,
            policy.programIds[0] ?? result.policy.programUuid ?? ""
        );
        updated.programIds = policy.programIds;
        queryClient.setQueryData<ProgramPoliciesResponse>(
            PROGRAM_POLICIES_QUERY_KEY,
            (prev) => {
                const base = prev ?? data;
                return {
                    ...base,
                    policies: base.policies.map((p) =>
                        p.id === policy.id ? updated : p
                    ),
                    snapshotAt: result.snapshotAt
                };
            }
        );
    };

    // Sub-category dropdown options — the unique policy TYPES within the
    // chosen category (a type may have several instances; it appears once).
    const typeOptions = Array.from(
        new Map(
            instances
                .filter((p) => category === "all" || p.category === category)
                .map((p) => [p.typeId, p.type] as const)
        ).entries()
    ).map(([typeId, label]) => ({ value: typeId, label }));
    const policyOptions: { value: string; label: string }[] = [
        { value: "all", label: "All Policies" },
        ...typeOptions
    ];

    const visible = instances.filter((p) => {
        const categoryMatch =
            category === "all" ? true : p.category === category;
        const policyMatch =
            policyFilter === "all" ? true : p.typeId === policyFilter;
        const active = policyIsActive(p, availablePrograms);
        const activityMatch =
            activity === "all"
                ? true
                : activity === "active"
                  ? active
                  : !active;
        return categoryMatch && policyMatch && activityMatch;
    });

    const selected =
        visible.find((p) => p.id === selectedId) ?? visible[0] ?? null;

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Page header */}
            <section className="py-6 border-b border-line-base shrink-0">
                <div className="px-6">
                    <Text variant="h1" className="text-foreground-base">
                        Program Policies
                    </Text>
                </div>
            </section>

            <div className="flex flex-1 min-h-0">
                {/* Left rail — filters + policy library */}
                <aside className="w-80 shrink-0 border-r border-line-base overflow-auto">
                    <div className="px-4 pt-5 pb-4 flex items-center justify-between gap-4">
                        <Text variant="small" className="text-foreground-muted">
                            {visible.length} polic
                            {visible.length === 1 ? "y" : "ies"}
                        </Text>
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="xs"
                            onClick={() => setCreating(true)}
                        >
                            +New Policy
                        </Button>
                    </div>

                    {/* Category dropdown */}
                    <div className="px-4 pb-3">
                        <FilterSelect
                            options={CATEGORY_OPTIONS}
                            value={category}
                            onChange={(v) => {
                                setCategory(v);
                                setPolicyFilter("all");
                            }}
                        />
                    </div>

                    {/* Policy (sub-category) dropdown — depends on category */}
                    <div className="px-4 pb-3">
                        <FilterSelect
                            options={policyOptions}
                            value={policyFilter}
                            onChange={setPolicyFilter}
                        />
                    </div>

                    {/* Active / Inactive status toggle */}
                    <div className="px-4 pb-5">
                        <ActivityToggle
                            value={activity}
                            onChange={setActivity}
                        />
                    </div>

                    {/* Sub-categories — the policies in the chosen category */}
                    {visible.length > 0 ? (
                        <List
                            selectable="single"
                            selected={selected?.id ?? ""}
                            onSelect={setSelectedId}
                        >
                            {visible.map((p) => (
                                <PolicyRow
                                    key={p.id}
                                    policy={p}
                                    programs={availablePrograms}
                                />
                            ))}
                        </List>
                    ) : (
                        <div className="px-4 py-8">
                            <Text
                                variant="small"
                                className="text-foreground-muted"
                            >
                                No policies in this view.
                            </Text>
                        </div>
                    )}
                </aside>

                {/* Detail — create form, or the selected policy */}
                {creating ? (
                    <CreatePolicyForm
                        programs={availablePrograms}
                        onCreate={handleCreate}
                        onCancel={() => setCreating(false)}
                    />
                ) : selected ? (
                    <PolicyDetail
                        key={selected.id}
                        policy={selected}
                        programs={availablePrograms}
                        onUpdate={handleUpdate}
                    />
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <Text variant="body" className="text-foreground-muted">
                            Select a policy to view its detail.
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
}

/** A dropdown filter (category). */
function FilterSelect<T extends string>({
    options,
    value,
    onChange
}: {
    options: { value: T; label: string }[];
    value: T;
    onChange: (value: T) => void;
}): React.ReactElement {
    const currentLabel = options.find((o) => o.value === value)?.label ?? "";
    return (
        <Select
            value={value}
            onValueChange={(v) => {
                if (v) onChange(v as T);
            }}
        >
            <SelectTrigger size="sm" className="w-full">
                <span className="truncate text-left">{currentLabel}</span>
            </SelectTrigger>
            <SelectContent align="start">
                {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                        {o.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/** Active / Inactive status toggle — the shared ToggleGroup primitive,
 *  styled as a connected segmented control. */
function ActivityToggle({
    value,
    onChange
}: {
    value: ActivityFilter;
    onChange: (value: ActivityFilter) => void;
}): React.ReactElement {
    return (
        <ToggleGroup
            variant="outline"
            spacing={2}
            value={[value]}
            onValueChange={(values) => {
                // Base UI emits an array; ignore deselect (always one filter).
                const next = values[0];
                if (typeof next === "string") {
                    onChange(next as ActivityFilter);
                }
            }}
        >
            {ACTIVITY_OPTIONS.map((o) => (
                <ToggleGroupItem key={o.value} value={o.value}>
                    {o.label}
                </ToggleGroupItem>
            ))}
        </ToggleGroup>
    );
}

function PolicyRow({
    policy,
    programs
}: {
    policy: PolicyDefinition;
    programs: Program[];
}): React.ReactElement {
    const active = policyIsActive(policy, programs);
    return (
        <ListItem itemKey={policy.id} padding="px-4 py-3" gap="gap-0">
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <IndicationUnit
                        variant="status"
                        state={active ? "success" : "disabled"}
                        size={7}
                    />
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-base block truncate"
                    >
                        {policy.type}
                    </Text>
                </div>
                <Text
                    variant="fine-print"
                    className="text-foreground-muted mt-1 pl-4 block truncate"
                >
                    {policy.value}
                </Text>
            </div>
        </ListItem>
    );
}

function StatusBadge({ active }: { active: boolean }): React.ReactElement {
    return (
        <span className="inline-flex items-center gap-2">
            <IndicationUnit
                variant="status"
                state={active ? "success" : "disabled"}
                size={6}
            />
            <Text
                variant="small"
                render={<span />}
                className={
                    active ? "text-success-base" : "text-foreground-muted"
                }
            >
                {active ? "Active" : "Inactive"}
            </Text>
        </span>
    );
}

function PolicyDetail({
    policy,
    programs: allPrograms,
    onUpdate
}: {
    policy: PolicyDefinition;
    programs: Program[];
    onUpdate: (policy: PolicyDefinition, raw: string) => Promise<void>;
}): React.ReactElement {
    const programs = programsForPolicy(policy, allPrograms);
    const attached = programs.length > 0;
    const active = policyIsActive(policy, allPrograms);

    return (
        <div className="flex-1 overflow-auto">
            {/* Title block — title + status, then category / type / permission */}
            <section className="py-8 border-b border-line-base">
                <div className="px-8">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Text
                            variant="h2"
                            className="text-foreground-base text-3xl"
                        >
                            {policy.type}
                        </Text>
                        <StatusBadge active={active} />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {/* Category + type are related → same color. */}
                        <Badge variant="septenary">
                            {CATEGORY_LABELS[policy.category]}
                        </Badge>
                        <Badge variant="septenary">{policy.type}</Badge>
                        {/* Permission is its own axis → distinct, off-olive color. */}
                        <Badge variant="quaternary">
                            {PERMISSION_LABELS[policy.permission]}
                        </Badge>
                    </div>
                </div>
            </section>

            {/* Configuration — editable in-line unless locked */}
            <section className="py-8 border-b border-line-base">
                <div className="px-8">
                    <Heading
                        title="Configuration"
                        titleVariant="h2"
                        subtitle={policy.detail}
                        subtitleVariant="body"
                        className="max-w-2xl"
                    />

                    <PolicyConfigEditor policy={policy} onSave={onUpdate} />
                </div>
            </section>

            {/* Programs attached to */}
            <section className="py-8">
                <div className="px-8">
                    <Text variant="h2" className="text-foreground-base">
                        Programs attached to
                    </Text>
                </div>
                {attached ? (
                    <div className="mt-5 border-t border-line-base divide-y divide-line-base">
                        {programs.map((program) => (
                            <ProgramRow key={program.id} program={program} />
                        ))}
                    </div>
                ) : (
                    <div className="px-8 mt-3">
                        <Text variant="small" className="text-foreground-muted">
                            Not attached to any program — 0 programs.
                        </Text>
                    </div>
                )}
            </section>
        </div>
    );
}

function ProgramRow({ program }: { program: Program }): React.ReactElement {
    const daysLeft = programDaysLeft(program);
    return (
        <div className="w-full px-8 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
                <IndicationUnit
                    variant="status"
                    state={PROGRAM_STATE[program.status]}
                    size={6}
                />
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-base block truncate"
                >
                    {program.name}
                </Text>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {/* Target — its own color. */}
                <Badge variant="secondary">Users</Badge>
                {/* Duration model + days remaining are related → same color. */}
                <Badge variant="senary">
                    {(program.programType ?? "fixed") === "continuous"
                        ? "Continuous"
                        : "Fixed"}
                </Badge>
                {daysLeft !== null && (
                    <Badge variant="senary">{daysLeft} Days Left</Badge>
                )}
            </div>
        </div>
    );
}

/**
 * Create-a-policy form (rendered in the detail pane). Pick a type, enter a
 * value **bounded by the issuer's constraint** (validated before Create is
 * allowed), and attach to programs. This is the client side of Jayesh's
 * "validation against issuer constraints" — adapted to programs.
 */
function CreatePolicyForm({
    programs,
    onCreate,
    onCancel
}: {
    programs: Program[];
    onCreate: (
        typeId: string,
        value: string,
        programIds: string[]
    ) => Promise<void>;
    onCancel: () => void;
}): React.ReactElement {
    const [typeId, setTypeId] = useState<string>(POLICY_TYPE_LIST[0].typeId);
    const [programIds, setProgramIds] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [summary, setSummary] =
        useState<PolicyEditorSummary>(EMPTY_CREATE_SUMMARY);
    const [resetSignal, setResetSignal] = useState(0);

    const type =
        POLICY_TYPE_LIST.find((t) => t.typeId === typeId) ??
        POLICY_TYPE_LIST[0];
    // Memoized: the editor re-reports its summary when its seed changes, and
    // a fresh `wallets` array every render would re-report forever.
    const draftPolicy = useMemo(
        () => createDraftPolicyDefinition(type),
        [type]
    );
    const entry = resolvePolicyEditor(draftPolicy);
    const Editor = entry.Editor;

    const toggleProgram = (id: string): void =>
        setProgramIds((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
        );

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            await onCreate(typeId, summary.serialized, programIds);
        } catch (error) {
            setSubmitError(
                resolveClientFormError(
                    error,
                    "policy",
                    "Policy was not created. Check the value and distributor constraints, then try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex-1 overflow-auto">
            <section className="py-8 border-b border-line-base">
                <div className="px-8">
                    <Text
                        variant="h2"
                        className="text-foreground-base text-3xl"
                    >
                        New Policy
                    </Text>
                    <Text
                        variant="fine-print"
                        className="text-foreground-imprint mt-1"
                    >
                        Create a policy within the distributor's constraints and
                        attach it to your programs.
                    </Text>
                </div>
            </section>

            <section className="py-8 border-b border-line-base">
                <div className="px-8 max-w-xl flex flex-col gap-6">
                    {/* Type */}
                    <Field label="Policy type">
                        <Select
                            value={typeId}
                            onValueChange={(v) => {
                                if (v) {
                                    setTypeId(v);
                                    setSubmitError(null);
                                    setSummary(EMPTY_CREATE_SUMMARY);
                                }
                            }}
                        >
                            <SelectTrigger size="sm" className="w-full">
                                <span className="truncate text-left">
                                    {type.label}
                                </span>
                            </SelectTrigger>
                            <SelectContent align="start">
                                {POLICY_TYPE_LIST.map((t) => (
                                    <SelectItem key={t.typeId} value={t.typeId}>
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Text
                            variant="fine-print"
                            className="text-foreground-muted"
                        >
                            {CATEGORY_LABELS[type.category]}
                        </Text>
                        <Text
                            variant="fine-print"
                            className="text-foreground-muted"
                        >
                            {type.detail}
                        </Text>
                    </Field>

                    {/* Value — rendered by the same type-specific editor used
                        for existing policy instances. */}
                    <Field label="Value">
                        <Editor
                            key={`${draftPolicy.typeId}:${entry.id}`}
                            policy={draftPolicy}
                            onSummaryChange={setSummary}
                            resetSignal={resetSignal}
                            locked={false}
                        />
                        <ConstraintSummary
                            permission={draftPolicy.permission}
                            constraint={draftPolicy.constraint}
                            minViolated={summary.minViolated}
                            maxViolated={summary.maxViolated}
                            className="mt-4"
                        />
                        {summary.errorLine ? (
                            <Text variant="fine-print" className="text-error">
                                {summary.errorLine}
                            </Text>
                        ) : null}
                    </Field>

                    {/* Attach to programs */}
                    <Field label="Attach to programs">
                        <div className="flex flex-col gap-2">
                            {programs.map((p) => {
                                const checked = programIds.includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => toggleProgram(p.id)}
                                        className="flex items-center gap-2 text-left"
                                    >
                                        <Checkbox
                                            checked={checked}
                                            className="pointer-events-none"
                                        />
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-foreground-base"
                                        >
                                            {p.name}
                                        </Text>
                                        <Text
                                            variant="fine-print"
                                            className="text-foreground-muted"
                                        >
                                            {p.statusLabel}
                                        </Text>
                                    </button>
                                );
                            })}
                        </div>
                        {programIds.length === 0 ? (
                            <Text
                                variant="fine-print"
                                className="text-foreground-muted"
                            >
                                Select at least one program.
                            </Text>
                        ) : null}
                    </Field>
                </div>
            </section>

            <section className="py-6">
                <div className="px-8 flex items-center gap-3">
                    <Button
                        variant="raised"
                        interaction="lifted"
                        size="md"
                        disabled={
                            !summary.valid ||
                            programIds.length === 0 ||
                            submitting
                        }
                        onClick={() => {
                            void submit();
                        }}
                    >
                        {submitting ? "Creating..." : "Create Policy"}
                    </Button>
                    <Button
                        variant="transparent"
                        size="md"
                        disabled={!summary.dirty || submitting}
                        onClick={() => {
                            summary.reset();
                            setResetSignal((n) => n + 1);
                        }}
                    >
                        Reset Value
                    </Button>
                    <Button variant="transparent" size="md" onClick={onCancel}>
                        Cancel
                    </Button>
                    {submitError ? (
                        <Text variant="fine-print" className="text-error">
                            {submitError}
                        </Text>
                    ) : null}
                </div>
            </section>
        </div>
    );
}

function Field({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <div className="flex flex-col gap-1.5">
            <Text variant="label" className="text-foreground-base">
                {label}
            </Text>
            {children}
        </div>
    );
}
